import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormMessage } from "@/components/ui/form-message";
import type { AdminTierUpdateInput } from "@/features/admin/admin.types";
import { validateTierUpdateInput } from "@/features/admin/adminValidation";

type ChangeTierDialogProps = {
  busy?: boolean;
  currentTier: AdminTierUpdateInput["tier"];
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: Omit<AdminTierUpdateInput, "userId">) => void;
  open: boolean;
};

export function ChangeTierDialog({
  busy = false,
  currentTier,
  onOpenChange,
  onSubmit,
  open,
}: ChangeTierDialogProps) {
  const { t } = useTranslation();
  const [tier, setTier] = useState<AdminTierUpdateInput["tier"]>(currentTier);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Partial<Record<"reason" | "tier", string>>>({});

  useEffect(() => {
    if (!open) {
      return;
    }

    setTier(currentTier);
    setReason("");
    setErrors({});
  }, [currentTier, open]);

  function handleSubmit() {
    const validationErrors = validateTierUpdateInput(
      {
        reason,
        tier,
      },
      currentTier,
    );
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    onSubmit({
      reason: reason.trim(),
      tier,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminWorkspace.dialogs.changeTier.title")}</DialogTitle>
          <DialogDescription>{t("adminWorkspace.dialogs.changeTier.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="space-y-2 text-sm text-[#d7cab9]/85">
            <span>{t("adminWorkspace.dialogs.changeTier.tierLabel")}</span>
            <select
              className="w-full rounded-3xl border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
              value={tier}
              onChange={(event) => setTier(event.target.value as AdminTierUpdateInput["tier"])}
            >
              <option value="free">{t("common.tiers.free")}</option>
              <option value="crafter">{t("common.tiers.crafter")}</option>
              <option value="author">{t("common.tiers.author")}</option>
            </select>
          </label>
          {errors.tier ? <FormMessage>{t(errors.tier)}</FormMessage> : null}
          <textarea
            className="min-h-28 w-full rounded-[1.75rem] border border-ink/10 bg-white/80 px-4 py-3 text-sm text-ink shadow-sm outline-none transition focus:border-clay/50 focus:ring-2 focus:ring-clay/20"
            placeholder={t("adminWorkspace.dialogs.changeTier.reasonPlaceholder")}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {errors.reason ? <FormMessage>{t(errors.reason)}</FormMessage> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button disabled={busy} type="button" onClick={handleSubmit}>
            {busy ? t("adminWorkspace.dialogs.changeTier.applying") : t("adminWorkspace.dialogs.changeTier.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
